import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../reduxService/hooks';
import { IDynamicProjectOutputData } from '../api/IApiResponse';
import { RootState } from '../../reduxService';

interface UseAutoFetchResult {
    autoFetchError: string | null;
    setAutoFetchError: (error: string | null) => void;
    clearAutoFetchError: () => void;
}

/**
 * Custom hook for handling Results page access without calculation data
 *
 * When user navigates directly to /results/{guid} (e.g., page reload):
 * - If no calculation results exist in Redux, redirect to /designCondition/{guid}
 * - User can then click RUN to re-calculate
 *
 * This applies to ALL project types (Paved, Airport, Railway, Unpaved, ContainerYard)
 * since calculation results are stored in Redux memory and lost on page reload.
 *
 * IMPORTANT: Does NOT redirect during programmed navigation (after clicking RUN)
 * because resultsData is updated asynchronously after navigation starts.
 *
 * Results storage locations:
 * - Paved/Airport: state.Dynamic.project.data.aashtoResults (frontend calculations)
 * - Railway/Unpaved/ContainerYard: state.Dynamic.results.data.outputData (backend calculations)
 */
export const useAutoFetch = (
    inResults: boolean,
    dynamicProjectData: { guid?: string; type?: string } | null,
    resultsData: IDynamicProjectOutputData | undefined
): UseAutoFetchResult => {
    const navigate = useNavigate();
    const [autoFetchError, setAutoFetchError] = useState<string | null>(null);

    // Check if we're in a programmed navigation (after clicking RUN)
    // This flag is set by useCalculationOrchestrator and cleared after navigation completes
    const isNavigatingToResults = useAppSelector((state: RootState) => state.Dynamic.project.data?.isNavigatingToResults ?? false);

    // Check for Paved/Airport results (frontend calculations store in aashtoResults)
    const aashtoResults = useAppSelector((state: RootState) => state.Dynamic.project.data?.aashtoResults);

    // Has any results (either backend outputData OR frontend aashtoResults)
    const hasAnyResults = !!resultsData || !!aashtoResults;

    // Redirect to DesignCondition if on Results page without calculation results
    // BUT NOT during programmed navigation (when user clicked RUN)
    useEffect(() => {
        if (inResults && dynamicProjectData?.guid && !hasAnyResults && !isNavigatingToResults) {
            console.log('🔄 useAutoFetch: Redirecting to DesignCondition (no results in Redux)', {
                guid: dynamicProjectData.guid,
                projectType: dynamicProjectData.type,
                hasOutputData: !!resultsData,
                hasAashtoResults: !!aashtoResults,
            });
            navigate(`/designCondition/${dynamicProjectData.guid}`);
        }
    }, [inResults, dynamicProjectData?.guid, dynamicProjectData?.type, hasAnyResults, navigate, isNavigatingToResults, resultsData, aashtoResults]);

    const clearAutoFetchError = useCallback(() => {
        setAutoFetchError(null);
    }, []);

    return {
        autoFetchError,
        setAutoFetchError,
        clearAutoFetchError,
    };
};
